import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  DatePickerDirective,
  DpDatePickerModule,
  ECalendarValue,
  IDatePickerDirectiveConfig,
  ISelectionEvent,
} from 'ng2-date-picker';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import de from 'dayjs/locale/de';
import dayjs from 'dayjs';
import { AbstractBasicSignalInput } from '../abstract-basic-signal-input.component';
import { TDrops, WeekDays } from '../../inputs/datepicker/datepicker.component';
import { FocusDirective, MaterialIconComponent } from '@ps-gui/shared-core';

dayjs.locale(de);

// export type DpCalendarValue = CalendarValue;
// export type DpISelectionEvent = ISelectionEvent;
// export type DpIDatePickerDirectiveConfig = IDatePickerDirectiveConfig;
//
// export type TDrops = 'up' | 'down';
// export type WeekDays = 'su' | 'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa';

@Component({
  //TODO: change selector when old datepicker component gets removed
  selector: 'shared-ui-datepicker-signal',
  standalone: true,
  imports: [
    NgClass,
    DpDatePickerModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialIconComponent,
    NgTemplateOutlet,
    FocusDirective,
  ],
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss',
})
export class DatepickerComponentSignal extends AbstractBasicSignalInput<string> implements OnInit {
  readonly returnValueType = input<ECalendarValue>(ECalendarValue.String);
  readonly placeholderText = input<string>('Datum auswählen');
  readonly drops = input<TDrops | undefined>(undefined);
  readonly format = input<string | undefined>('DD.MM.YYYY');

  readonly additionalCloseCondition = input<boolean>(false);
  readonly additionalOpenCondition = input<boolean>(false);

  closeEvent = output();
  focusEvent = output<FocusEvent>();
  selectEvent = output();

  readonly dateDirectivePicker = viewChild<DatePickerDirective>('datePickerDirective');
  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  private readonly formBuilder = inject(FormBuilder);

  readonly datePickerConfig: IDatePickerDirectiveConfig = {
    format: this.format(),
    drops: this.drops(),
    returnedValueType: this.returnValueType(),
    showGoToCurrent: false,
    firstDayOfWeek: 'mo' as WeekDays,
    showMultipleYearsNavigation: true,
    inputElementContainer: this.inputElement(),
  };

  override ngOnInit(): void {
    super.ngOnInit();
    this.initControl();

    this.control?.valueChanges.subscribe((dateString) => {
      if (dateString === '') {
        this.value.set(dateString);
        return;
      }
      if (this.isValidDateWithoutDots(dateString)) {
        const parsedDateString = dayjs(dateString, 'DDMMYYYY', true).format('DD.MM.YYYY');
        this.control?.setValue(parsedDateString, {
          emitEvent: false,
        });

        this.updateFieldStateWithtLocalizedDate(parsedDateString);
        return;
      }

      if (this.isValidDateWithoutDotsAndShortYear(dateString)) {
        const parsedDateString = dayjs(dateString, 'DDMMYY', true).format('DD.MM.YYYY');
        this.control?.setValue(parsedDateString, {
          emitEvent: false,
        });

        this.updateFieldStateWithtLocalizedDate(parsedDateString);
        return;
      }

      const isValid = dayjs(dateString, 'DD.MM.YYYY', true).isValid();
      if (!isValid) {
        this.value.set(dateString);
        return;
      }
      this.updateFieldStateWithtLocalizedDate(dateString);
    });
  }

  updateFieldStateWithtLocalizedDate(germanDateString: string) {
    const localDateString = dayjs(germanDateString, 'DD.MM.YYYY', true).format('YYYY-MM-DD');
    this.value.set(localDateString);
  }

  constructor() {
    super();
    effect(() => {
      if (!this.additionalOpenCondition()) return;

      this.dateDirectivePicker()?.api?.open();
    });

    effect(() => {
      if (!this.additionalCloseCondition()) return;

      this.dateDirectivePicker()?.api?.close();
    });

    effect(() => {
      if (!this.control) return;
      const newFieldStateValue = this.value();

      if (newFieldStateValue === '') {
        this.control.setValue('', { emitEvent: false });
        return;
      }

      const dayjsDate = dayjs(newFieldStateValue, 'YYYY-MM-DD', true);
      if (!dayjsDate.isValid()) {
        this.control.setValue(newFieldStateValue, { emitEvent: false });
        return;
      }

      const germanDate = dayjsDate.format('DD.MM.YYYY');
      if (germanDate === this.control.value) return;

      this.control.setValue(germanDate, { emitEvent: false });
    });

    effect(() => {
      if (this.disabled()) {
        this.control?.disable({ emitEvent: false });
        return;
      }
      this.control?.enable({ emitEvent: false });
    });
  }

  readonly isOpened = signal(false);

  onDatePickerOpen() {
    this.dateDirectivePicker()?.api?.moveCalendarTo(
      this.control?.value ? this.control?.value : dayjs(),
    );
  }

  initControl() {
    const isBlank = this.value() === '';
    const dateString = !isBlank
      ? dayjs(this.value(), 'YYYY-MM-DD').format('DD.MM.YYYY')
      : this.value();

    this.control = this.formBuilder.nonNullable.control(dateString, null);
  }

  control: FormControl<string> | undefined = undefined;

  private isValidDateWithoutDots(value: string | null): boolean {
    const date = dayjs(value, 'DDMMYYYY', true);

    return date.isValid();
  }

  private isValidDateWithoutDotsAndShortYear(value: string | null): boolean {
    const date = dayjs(value, 'DDMMYY', true);

    return date.isValid();
  }

  onSelect(event: ISelectionEvent) {
    if (event.type !== 'selection') return;

    this.selectEvent.emit();
  }
}
